import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { PhotoCapture } from "../components/PhotoCapture";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2, ArrowLeft, FilePlus2, LayoutDashboard, CheckCircle2 } from "lucide-react";

export default function AddContractor() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({
    first_name: "", last_name: "", company: "", email: "", dbs_number: "", mobile: "",
    emergency_contact: "", notes: "", contractor_photo: null,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.first_name || !form.last_name) return toast.error("Enter first and last name.");
    if (!form.company) return toast.error("Enter the company.");
    setSaving(true);
    try {
      const { data } = await api.post("/contractors", form);
      setCreated(data);
      setDoneOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <button onClick={() => navigate("/")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-brand" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to contractors
      </button>
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">New Contractor</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Add contractor</h1>
        <p className="text-sm text-muted-foreground">Register the contractor's details. Site inductions are added afterwards from their profile.</p>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <div className="rounded-md border border-border bg-white p-5 shadow-sm">
            <div className="grid grid-cols-2 gap-4">
              <FieldText label="First name" testid="c-first" value={form.first_name} onChange={(v) => set("first_name", v)} />
              <FieldText label="Last name" testid="c-last" value={form.last_name} onChange={(v) => set("last_name", v)} />
              <FieldText label="Company / Organisation" testid="c-company" value={form.company} onChange={(v) => set("company", v)} />
              <FieldText label="Email address" testid="c-email" value={form.email} onChange={(v) => set("email", v)} />
              <FieldText label="DBS Certificate number" testid="c-dbs" value={form.dbs_number} onChange={(v) => set("dbs_number", v)} />
              <FieldText label="Mobile number" testid="c-mobile" value={form.mobile} onChange={(v) => set("mobile", v)} />
              <div className="col-span-2">
                <FieldText label="Emergency contact" testid="c-emergency" value={form.emergency_contact} onChange={(v) => set("emergency_contact", v)} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Notes / medical conditions</Label>
                <Textarea data-testid="c-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any medical conditions or notes the site needs to be aware of" />
              </div>
            </div>
          </div>

          <Button className="mt-6 w-full bg-brand hover:bg-brand-dark" size="lg" onClick={submit} disabled={saving} data-testid="submit-contractor">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save contractor
          </Button>
        </div>

        <div className="lg:col-span-4">
          <div className="sticky top-24">
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Contractor photo (optional)</div>
            <PhotoCapture value={form.contractor_photo} onCapture={(v) => set("contractor_photo", v)} />
          </div>
        </div>
      </div>

      {/* Saved — next step dialog */}
      <Dialog open={doneOpen} onOpenChange={setDoneOpen}>
        <DialogContent className="max-w-md" data-testid="contractor-saved-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <CheckCircle2 className="h-5 w-5 text-green-600" /> Contractor saved
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {created?.name} ({created?.reference}) has been added. What would you like to do next?
          </p>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" className="w-full sm:w-auto" onClick={() => navigate("/")} data-testid="saved-dashboard-btn">
              <LayoutDashboard className="mr-2 h-4 w-4" /> Back to dashboard
            </Button>
            <Button className="w-full bg-brand hover:bg-brand-dark sm:w-auto" onClick={() => navigate(`/contractors/${created.id}/issue`)} data-testid="saved-add-permit-btn">
              <FilePlus2 className="mr-2 h-4 w-4" /> Add a permit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function FieldText({ label, testid, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
